import React, { useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Newspaper,
    PlusCircle,
    Users,
    ShieldCheck,
    Settings,
    LogOut,
    Menu,
    Sparkles,
    Store,
    ClipboardList,
    IndianRupee,
    TrendingUp,
    Layers,
    FileText,
    Ticket,
    BarChart3,
    Megaphone,
    Video,
    ImagePlus,
    Star,
    Calculator,
    Receipt,
    PieChart,
    Bot,
    Link2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/shared/Logo";
import { RoleBadge } from "@/components/shared/Badges";
import { cn } from "@/lib/utils";
import { NotificationBell } from "@/components/promo/NotificationBell";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

// Nav taxonomy — same items as before, now organized into visual groups.
const NAV_BY_ROLE = {
    SUPER_ADMIN: [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/posts", label: "Public Feed", icon: Newspaper },
        { to: "/admin/posts", label: "All Posts", icon: ClipboardList },
        { to: "/posts/new", label: "Create Post", icon: PlusCircle },
        { to: "/admin/users", label: "Users", icon: Users },
        { to: "/admin/shops/pending", label: "Pending Shops", icon: ShieldCheck },
        { to: "/me/posts", label: "My Posts", icon: Store },
        { to: "/smart-home", label: "Smart Home", icon: Sparkles },
        { to: "/admin/partner-shops", label: "Partner Shops", icon: Store, section: "Master Data" },
        { to: "/admin/waitlist", label: "Waitlist", icon: ClipboardList, section: "Growth" },
        { to: "/admin/analytics", label: "Analytics", icon: BarChart3, section: "Growth" },
        { to: "/admin/partner-brands", label: "Partner Brands", icon: Store, section: "Growth" },
        { to: "/admin/revenue", label: "Revenue", icon: IndianRupee, section: "Revenue" },
        { to: "/admin/revenue/packages", label: "Packages", icon: Layers, section: "Revenue" },
        { to: "/admin/revenue/subscriptions", label: "Subscriptions", icon: TrendingUp, section: "Revenue" },
        { to: "/admin/revenue/transactions", label: "Transactions", icon: FileText, section: "Revenue" },
        { to: "/admin/revenue/coupon-topups", label: "Coupon Top-ups", icon: Ticket, section: "Revenue" },
        { to: "/admin/revenue/reports", label: "Reports", icon: BarChart3, section: "Revenue" },
        { to: "/admin/ads", label: "Ads Overview", icon: Megaphone, section: "Ads" },
        { to: "/admin/merchant-ads", label: "Merchant Ads", icon: Megaphone, section: "Ads" },
        { to: "/admin/ads/banners", label: "Banners", icon: ImagePlus, section: "Ads" },
        { to: "/admin/ads/videos", label: "Video Queue", icon: Video, section: "Ads" },
        { to: "/admin/ads/sponsored", label: "Sponsored", icon: Star, section: "Ads" },
        { to: "/admin/finance", label: "Finance", icon: Calculator, section: "Finance" },
        { to: "/admin/finance/expenses", label: "Expenses", icon: Receipt, section: "Finance" },
        { to: "/admin/finance/pnl", label: "P&L", icon: PieChart, section: "Finance" },
        { to: "/admin/finance/ai-accountant", label: "AI Accountant", icon: Bot, section: "Finance" },
        { to: "/settings", label: "Settings", icon: Settings },
    ],
    FRANCHISE_OWNER: [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/posts", label: "Public Feed", icon: Newspaper },
        { to: "/admin/posts", label: "All Posts", icon: ClipboardList },
        { to: "/posts/new", label: "Create Post", icon: PlusCircle },
        { to: "/me/posts", label: "My Posts", icon: Store },
        { to: "/settings", label: "Settings", icon: Settings },
    ],
    DISTRICT_MANAGER: [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/posts", label: "Public Feed", icon: Newspaper },
        { to: "/admin/posts", label: "All Posts", icon: ClipboardList },
        { to: "/posts/new", label: "Create Post", icon: PlusCircle },
        { to: "/me/posts", label: "My Posts", icon: Store },
        { to: "/settings", label: "Settings", icon: Settings },
    ],
    SUPERVISOR: [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/posts", label: "Public Feed", icon: Newspaper },
        { to: "/admin/posts", label: "All Posts", icon: ClipboardList },
        { to: "/posts/new", label: "Create Post", icon: PlusCircle },
        { to: "/me/posts", label: "My Posts", icon: Store },
        { to: "/settings", label: "Settings", icon: Settings },
    ],
    STAFF: [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/posts", label: "Public Feed", icon: Newspaper },
        { to: "/posts/new", label: "Create Post", icon: PlusCircle },
        { to: "/me/posts", label: "My Drafts", icon: Store },
        { to: "/settings", label: "Settings", icon: Settings },
    ],
    ACCOUNTS: [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/posts", label: "Public Feed", icon: Newspaper },
        { to: "/admin/waitlist", label: "Waitlist", icon: ClipboardList, section: "Growth" },
        { to: "/admin/analytics", label: "Analytics", icon: BarChart3, section: "Growth" },
        { to: "/admin/revenue", label: "Revenue", icon: IndianRupee, section: "Revenue" },
        { to: "/admin/revenue/packages", label: "Packages", icon: Layers, section: "Revenue" },
        { to: "/admin/revenue/subscriptions", label: "Subscriptions", icon: TrendingUp, section: "Revenue" },
        { to: "/admin/revenue/transactions", label: "Transactions", icon: FileText, section: "Revenue" },
        { to: "/admin/revenue/coupon-topups", label: "Coupon Top-ups", icon: Ticket, section: "Revenue" },
        { to: "/admin/revenue/reports", label: "Reports", icon: BarChart3, section: "Revenue" },
        { to: "/admin/ads", label: "Ads Overview", icon: Megaphone, section: "Ads" },
        { to: "/admin/ads/banners", label: "Banners", icon: ImagePlus, section: "Ads" },
        { to: "/admin/ads/videos", label: "Video Queue", icon: Video, section: "Ads" },
        { to: "/admin/finance", label: "Finance", icon: Calculator, section: "Finance" },
        { to: "/admin/finance/expenses", label: "Expenses", icon: Receipt, section: "Finance" },
        { to: "/admin/finance/pnl", label: "P&L", icon: PieChart, section: "Finance" },
        { to: "/admin/finance/ai-accountant", label: "AI Accountant", icon: Bot, section: "Finance" },
        { to: "/admin/reports/partner-clicks", label: "Partner Clicks", icon: Link2, section: "Finance" },
        { to: "/admin/trending", label: "Trending", icon: TrendingUp, section: "Finance" },
        { to: "/settings", label: "Settings", icon: Settings },
    ],
    SHOP_OWNER: [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/posts", label: "Public Feed", icon: Newspaper },
        { to: "/posts/new", label: "Create Offer", icon: PlusCircle },
        { to: "/me/posts", label: "My Offers", icon: Store },
        { to: "/settings", label: "Settings", icon: Settings },
    ],
};

/** Nav row — Phase 13c light sidebar. Active state uses a warm orange chip
 * plus an orange left-stripe; inactive rows are muted grey with a subtle
 * hover tint. */
const NavItem = ({ to, label, icon: Icon, onClick }) => (
    <NavLink
        to={to}
        end={to === "/dashboard"}
        onClick={onClick}
        data-testid={`nav-${to.replace(/[/]/g, "-").replace(/^-/, "")}`}
        className={({ isActive }) =>
            cn(
                "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 font-heading text-sm font-medium transition",
                isActive
                    ? "bg-[#FFF3E6] text-[var(--brand-orange)]"
                    : "text-[var(--text-muted)] hover:bg-slate-100 hover:text-[var(--text-primary)]",
            )
        }
    >
        {({ isActive }) => (
            <>
                <span
                    aria-hidden
                    className={cn(
                        "absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full transition",
                        isActive ? "bg-[var(--brand-orange)]" : "bg-transparent",
                    )}
                />
                <Icon size={18} className={cn("opacity-90", isActive && "text-[var(--brand-orange)]")} />
                <span>{label}</span>
            </>
        )}
    </NavLink>
);

const SidebarContent = ({ user, onClickItem, onLogout }) => {
    const items = NAV_BY_ROLE[user?.role] || NAV_BY_ROLE.ACCOUNTS;
    // Group items so we can render section headers between them.
    const grouped = [];
    let currentSection = null;
    items.forEach((it) => {
        if (it.section && it.section !== currentSection) {
            grouped.push({ header: it.section });
            currentSection = it.section;
        } else if (!it.section) {
            currentSection = null;
        }
        grouped.push(it);
    });

    return (
        <div className="flex h-full flex-col border-r border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-5 py-4">
                <Logo size={48} />
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4" data-testid="admin-sidebar-nav">
                {grouped.map((row, i) =>
                    row.header ? (
                        <div
                            key={`h-${row.header}-${i}`}
                            className="mt-4 px-3 pb-1 font-heading text-[11px] font-semibold uppercase tracking-wider text-slate-400"
                        >
                            {row.header}
                        </div>
                    ) : (
                        <NavItem key={row.to} {...row} onClick={onClickItem} />
                    ),
                )}
            </nav>
            <div className="border-t border-slate-200 p-3">
                <button
                    onClick={onLogout}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 font-heading text-sm font-medium text-[var(--text-muted)] transition hover:bg-slate-100 hover:text-rose-600"
                    data-testid="sidebar-logout-btn"
                >
                    <LogOut size={18} /> Log out
                </button>
            </div>
        </div>
    );
};

export const AppShell = ({ children }) => {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    return (
        <div className="flex min-h-screen w-full bg-surface">
            {/* Desktop sidebar */}
            <aside className="hidden w-64 shrink-0 lg:block">
                <div className="fixed inset-y-0 left-0 w-64">
                    <SidebarContent user={user} onLogout={logout} />
                </div>
            </aside>

            <div className="flex min-h-screen w-full min-w-0 flex-1 flex-col lg:pl-64">
                {/* Top bar */}
                <header
                    className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/85 px-4 backdrop-blur-md lg:px-8"
                    data-testid="admin-topbar"
                >
                    <div className="flex items-center gap-3">
                        {/* Mobile hamburger */}
                        <Sheet open={open} onOpenChange={setOpen}>
                            <SheetTrigger asChild>
                                <button
                                    className="rounded-lg p-2 text-[var(--text-muted)] hover:bg-slate-100 lg:hidden"
                                    aria-label="Open menu"
                                    data-testid="mobile-menu-btn"
                                >
                                    <Menu size={22} />
                                </button>
                            </SheetTrigger>
                            <SheetContent
                                side="left"
                                className="w-72 p-0 border-none"
                                data-testid="mobile-sidebar"
                            >
                                <SidebarContent
                                    user={user}
                                    onClickItem={() => setOpen(false)}
                                    onLogout={() => {
                                        setOpen(false);
                                        logout();
                                    }}
                                />
                            </SheetContent>
                        </Sheet>
                        <Link to="/dashboard" className="lg:hidden">
                            <Logo size={44} />
                        </Link>
                    </div>

                    <div className="flex items-center gap-3">
                        <NotificationBell />
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className="flex items-center gap-3 rounded-full border border-slate-200 bg-white px-2 py-1.5 pr-3 shadow-sm hover:bg-slate-50"
                                    data-testid="topbar-profile-btn"
                                >
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--brand-orange)] font-heading text-sm font-bold text-white">
                                        {(user?.full_name || "?")
                                            .split(" ")
                                            .map((s) => s[0])
                                            .slice(0, 2)
                                            .join("")
                                            .toUpperCase()}
                                    </span>
                                    <div className="hidden text-left sm:block">
                                        <div
                                            className="max-w-[10rem] truncate font-heading text-sm font-semibold text-[var(--text-primary)]"
                                            data-testid="topbar-user-name"
                                        >
                                            {user?.full_name || "User"}
                                        </div>
                                        <div className="text-xs text-[var(--text-muted)]">
                                            {user?.email}
                                        </div>
                                    </div>
                                    <RoleBadge role={user?.role} className="hidden sm:inline-flex" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>
                                    <div className="font-heading text-sm font-semibold">
                                        {user?.full_name}
                                    </div>
                                    <div className="truncate text-xs text-[var(--text-muted)]">
                                        {user?.email}
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => navigate("/settings")}
                                    data-testid="menu-settings"
                                >
                                    <Settings size={16} className="mr-2" />
                                    Profile & Settings
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={logout}
                                    data-testid="menu-logout"
                                    className="text-rose-600"
                                >
                                    <LogOut size={16} className="mr-2" />
                                    Log out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                {/* Content */}
                <main
                    className="min-w-0 flex-1 px-4 pb-24 pt-6 sm:px-6 lg:px-10 lg:pb-10"
                    data-testid="main-content"
                    key={location.pathname}
                >
                    {children}
                </main>

                {/* Mobile bottom nav for top-4 items */}
                <MobileBottomNav user={user} />
            </div>
        </div>
    );
};

const MobileBottomNav = ({ user }) => {
    const items = (NAV_BY_ROLE[user?.role] || []).slice(0, 4);
    if (!items.length) return null;
    return (
        <nav
            className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-md lg:hidden"
            data-testid="mobile-bottom-nav"
        >
            <div className="grid grid-cols-4">
                {items.map((it) => (
                    <NavLink
                        key={it.to}
                        to={it.to}
                        end={it.to === "/dashboard"}
                        data-testid={`bottomnav-${it.to.replace(/\//g, "-")}`}
                        className={({ isActive }) =>
                            cn(
                                "flex flex-col items-center gap-1 py-2.5 font-heading text-[11px] font-medium transition",
                                isActive
                                    ? "text-[var(--brand-orange)]"
                                    : "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                            )
                        }
                    >
                        <it.icon size={20} />
                        <span className="max-w-[70px] truncate">{it.label}</span>
                    </NavLink>
                ))}
            </div>
        </nav>
    );
};

export default AppShell;
